/**
 * CustomerLayout.jsx
 * Elegant, calming wrapper for customer wedding dashboard
 * Design direction: Warm, wedding-friendly, not corporate
 */

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Home, 
  UtensilsCrossed, 
  BedDouble, 
  CalendarDays, 
  Mail, 
  Palette,
  LogOut,
  Menu,
  X,
  Heart,
  Phone,
  HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";

const CustomerLayout = ({ children, customer, wedding }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("customer_token");
    localStorage.removeItem("customer_data");
    navigate("/my-wedding");
  };

  const navItems = [
    { path: "/my-wedding/dashboard", label: "Home", icon: Home },
    { path: "/my-wedding/food-menu", label: "Food Menu", icon: UtensilsCrossed },
    { path: "/my-wedding/rooms", label: "Rooms", icon: BedDouble },
    { path: "/my-wedding/itinerary", label: "Itinerary", icon: CalendarDays },
    { path: "/my-wedding/invite", label: "Invite Page", icon: Mail },
    { path: "/my-wedding/decor", label: "Decor", icon: Palette },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDF8F5] via-[#FAF5F2] to-[#F5EDE8]">
      {/* Top Header Bar */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E8DED5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Wedding Name */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A87C] to-[#B08968] flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-[#5C4A3D]">
                  {wedding?.couple_names || "Your Wedding"}
                </p>
                <p className="text-xs text-[#8B7355]">
                  {wedding?.wedding_date ? `${wedding.wedding_date}` : "Planning in progress"}
                </p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    isActive(item.path)
                      ? "bg-[#C9A87C]/20 text-[#8B6914]"
                      : "text-[#5C4A3D] hover:bg-[#F5EDE8]"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </span>
                </button>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="hidden sm:flex text-[#5C4A3D] hover:bg-[#F5EDE8]"
              >
                <HelpCircle className="w-4 h-4 mr-1" />
                Help
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-[#5C4A3D] hover:bg-[#F5EDE8]"
              >
                <LogOut className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
              
              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-[#F5EDE8]"
              >
                {isMobileMenuOpen ? (
                  <X className="w-5 h-5 text-[#5C4A3D]" />
                ) : (
                  <Menu className="w-5 h-5 text-[#5C4A3D]" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-[#E8DED5] bg-white/95 backdrop-blur-md">
            <nav className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive(item.path)
                      ? "bg-[#C9A87C]/20 text-[#8B6914]"
                      : "text-[#5C4A3D] hover:bg-[#F5EDE8]"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white/60 border-t border-[#E8DED5] mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-[#8B7355]">
              <Heart className="w-4 h-4 text-[#C9A87C]" />
              <span className="text-sm">Crafted with love by Happy Retreats</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-[#8B7355]">
              <a href="tel:+919876543210" className="flex items-center gap-1 hover:text-[#5C4A3D]">
                <Phone className="w-4 h-4" />
                <span>+91 98765 43210</span>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CustomerLayout;
